<script>
import { v4 as uuidv4 } from "uuid";

export default {
  props: {
    modalID: String,
    modalTitle: String,
    initialFiles: {
      type: Array,
      default: () => []
    },
  },

  data() {
    return {
      fileUploads: [],
      isDragging: false,
      loading: false,
    };
  },

  methods: {
    /* ============================= */
    /* File Handling                 */
    /* ============================= */

    selectFile() {
      this.$refs.uploadFile.click();
    },

    onFileChange() {
      const files = Array.from(this.$refs.uploadFile.files);

      const allowedTypes = ['application/pdf', 'image/png', 'image/jpg', 'image/jpeg'];
      const invalidFiles = files.filter(f => !allowedTypes.includes(f.type));

      if (invalidFiles.length) {
        this.$swal({
          icon: 'error',
          title: 'Invalid file type',
          text: 'Only PDF and Images are accepted.'
        });
        this.$refs.uploadFile.value = '';
        return;
      }

      const mapped = files.map(file => ({
        id: uuidv4(),
        file,
        name: file.name,
        size: file.size,
      }));

      this.fileUploads.push(...mapped);
    },

    removeFile(index) {
      this.fileUploads.splice(index, 1);
    },

    dragover(e) {
      e.preventDefault();
      this.isDragging = true;
    },

    dragleave() {
      this.isDragging = false;
    },

    drop(e) {
      e.preventDefault();
      this.isDragging = false;

      const files = Array.from(e.dataTransfer.files);

      const allowedTypes = ['application/pdf', 'image/png', 'image/jpg', 'image/jpeg'];
      const invalidFiles = files.filter(f => !allowedTypes.includes(f.type));

      if (invalidFiles.length) {
        this.$swal({
          icon: 'error',
          title: 'Invalid file type',
          text: 'Only PDF and Images are accepted.'
        });
        return;
      }

      const mapped = files.map(file => ({
        id: uuidv4(),
        file,
        name: file.name,
        size: file.size,
      }));

      this.fileUploads.push(...mapped);
    },

    /* ============================= */
    /* Smart Upload                     */
    /* ============================= */

    async handleUploadToAi() {
      if (!this.fileUploads.length) return;

      this.loading = true;

      try {
        // Fire all uploads in parallel — SignalR tracks per-file progress
        const uploads = this.fileUploads.map(item =>
          this.$store.dispatch("aiFileManager/smartUpload/uploadFile", {
            file: item.file,
            fileId: uuidv4(),
          })
        );

        // Close modal immediately — panel shows real-time progress
        this.$bvModal.hide(this.modalID);
        this.$toast.success("Files queued for upload & AI analysis!");

        await Promise.allSettled(uploads);
      } catch (err) {
        console.error("Smart Upload Error:", err);
      }

      this.loading = false;
    },

    onShownModal(){
      if (this.initialFiles && this.initialFiles.length > 0) {
        const newFiles = this.initialFiles.map(file => ({
          id: uuidv4(),
          file: file,
          name: file.name,
          size: file.size,
          progress: 0,
          completed: false,
          error: null,
          xhr: null,
          isUploading: false,
        }));

        this.fileUploads.push(...newFiles);
      }
    },

    onHiddenModal() {
      this.fileUploads = [];
      this.loading = false;
      this.isDragging = false;
    },
  },
};
</script>

<template>
  <b-modal
    :id="modalID"
    :title="modalTitle"
    centered
    scrollable
    @shown="onShownModal"
    @hidden="onHiddenModal"
  >
    <!-- DROPZONE -->
    <div
      class="ai-dropzone-wrapper"
      :class="{ 'ai-dropzone-active': isDragging }"
      @dragover.prevent="dragover"
      @dragleave="dragleave"
      @drop="drop"
    >
      <input
        ref="uploadFile"
        type="file"
        accept="image/png,image/jpeg,application/pdf"
        multiple
        hidden
        @change="onFileChange"
      />

      <!-- EMPTY STATE -->
      <div
        v-if="!fileUploads.length"
        class="ai-dropzone-empty text-center"
        @click="selectFile"
      >
        <div class="ai-dropzone-icon-ring">
          <i class="mdi mdi-brain font-size-24 text-white"></i>
        </div>

        <div class="mt-3">
          <strong>Drag & drop files here</strong><br />
          <small class="text-muted">or click to browse</small>
        </div>

        <small class="text-muted d-block mt-2">
          Supported: PDF, PNG, JPG — Multiple files allowed
        </small>
      </div>

      <!-- FILE LIST -->
      <div v-else>
        <div class="d-flex justify-content-between mb-2">
          <small class="text-muted">
            {{ fileUploads.length }} file(s) selected
          </small>

          <button
            type="button"
            class="btn btn-sm btn-soft-primary"
            @click="selectFile"
          >
            Add More
          </button>
        </div>

        <div class="ai-file-grid">
          <div
            v-for="(f, index) in fileUploads"
            :key="f.id"
            class="ai-file-card"
          >
            <div class="d-flex align-items-center">
              <div class="flex-grow-1" style="min-width: 0;">
                <div class="text-truncate fw-medium" :title="f.name">
                  {{ f.name }}
                </div>
                <small class="text-muted">
                  {{ (f.size / 1024).toFixed(1) }} KB
                </small>
              </div>

              <button
                type="button"
                class="btn btn-sm btn-soft-danger"
                @click="removeFile(index)"
              >
                <i class="mdi mdi-close"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- FOOTER -->
    <template #modal-footer="{ cancel }">
      <b-button variant="light" @click="cancel" class="me-2">
        Cancel
      </b-button>

      <b-button
        variant="primary"
        :disabled="loading || !fileUploads.length"
        @click="handleUploadToAi"
      >
        <b-spinner v-if="loading" small class="me-1"></b-spinner>
        <span class="text-white">Upload & Analyze</span>
      </b-button>
    </template>
  </b-modal>
</template>

<style scoped>
.ai-dropzone-wrapper {
  border: 2px dashed #c2d5f7;
  border-radius: 12px;
  padding: 1.5rem;
  min-height: 180px;
  transition: 0.2s ease;
}

.ai-dropzone-active {
  border-color: #4285f4;
  background: #f0f6ff;
}

.ai-dropzone-empty {
  cursor: pointer;
}

.ai-dropzone-icon-ring {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4285f4 0%, #7b68ee 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.ai-file-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.5rem;
}

.ai-file-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.75rem;
  background: #fff;
}

.btn-soft-primary {
  background: #e8f0fe;
  color: #4285f4;
  border: none;
}

.btn-soft-danger {
  background: #fef2f2;
  color: #dc2626;
  border: none;
}
</style>